import { useEffect } from 'react';

export default function PaiementSecurise() {
  useEffect(() => {
    document.title = 'Paiement sécurisé via Stripe | Les Carnets de Lolotte';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Paiement 100% sécurisé via Stripe sur Les Carnets de Lolotte. Données cryptées, aucune donnée bancaire stockée. CB Visa, Mastercard acceptées.');
    }

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Paiement sécurisé – Les Carnets de Lolotte',
      description: 'Paiement 100% sécurisé via Stripe. Données cryptées, aucune donnée bancaire stockée.',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/paiement-securise`,
      publisher: {
        '@type': 'Organization',
        name: 'Les Carnets de Lolotte',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Hero Section */}
      <section 
        className="py-16 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(248,237,234,0.9), rgba(248,237,234,0.9)), url('https://public.readdy.ai/ai/img_res/b401b7e00e88ea2e3882b364150313d3.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-shield-check-line text-3xl text-white"></i>
          </div>
          <h1 className="text-6xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
            Paiement sécurisé
          </h1>
          <p className="text-xl text-[#6B5E5A] max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Achetez en toute sérénité sur Les Carnets de Lolotte. 
            Vos paiements sont protégés par les technologies les plus avancées.
          </p>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section 1: Paiement 100% sécurisé */}
          <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#B97A72] p-8 mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mr-4">
                <i className="ri-secure-payment-line text-xl text-white"></i>
              </div>
              <h2 className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Allura, cursive' }}>
                💫 Paiement 100 % sécurisé
              </h2>
            </div>
            <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="text-lg leading-relaxed">
                Les paiements sur <em>Les Carnets de Lolotte</em> sont gérés par <strong>Stripe</strong>, une solution internationale reconnue pour sa fiabilité et sa sécurité.
              </p>
              <p className="text-lg leading-relaxed">
                Stripe protège vos informations bancaires grâce à un système de <strong>cryptage SSL (Secure Socket Layer)</strong>.
                Cela signifie que vos données sont totalement confidentielles et <strong>ne transitent jamais directement par le site</strong>.
              </p>
            </div>
          </div>

          {/* Section 2: Moyens de paiement */}
          <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#B97A72] p-8 mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mr-4">
                <i className="ri-bank-card-line text-xl text-white"></i>
              </div>
              <h2 className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Allura, cursive' }}>
                💳 Moyens de paiement acceptés
              </h2>
            </div>
            <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="text-lg leading-relaxed">
                Vous pouvez régler vos achats en toute sécurité par <strong>carte bancaire</strong> :
                Visa, Mastercard, American Express.
              </p>
              <p className="text-lg leading-relaxed">
                Les paiements sont <strong>instantanés</strong> et <strong>confirmés immédiatement</strong>.
                Vous recevrez un e-mail de confirmation dès la validation de votre commande.
              </p>
            </div>
            
            {/* Icônes des cartes */}
            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="w-16 h-16 bg-[#B97A72] rounded-lg flex items-center justify-center">
                <i className="ri-visa-line text-2xl text-white"></i>
              </div>
              <div className="w-16 h-16 bg-[#B97A72] rounded-lg flex items-center justify-center">
                <i className="ri-mastercard-line text-2xl text-white"></i>
              </div>
              <div className="w-16 h-16 bg-[#B97A72] rounded-lg flex items-center justify-center">
                <i className="ri-bank-card-line text-2xl text-white"></i>
              </div>
            </div>
          </div>

          {/* Section 3: Protection des données */}
          <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#B97A72] p-8 mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mr-4">
                <i className="ri-lock-line text-xl text-white"></i>
              </div>
              <h2 className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Allura, cursive' }}>
                🔒 Protection des données
              </h2>
            </div>
            <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="text-lg leading-relaxed">
                Aucune donnée bancaire n'est stockée sur le site <em>Les Carnets de Lolotte</em>.
                Toutes les transactions sont traitées via les serveurs sécurisés de Stripe, conformes aux normes internationales <strong>PCI DSS</strong>.
              </p>
              <p className="text-lg leading-relaxed">
                Ce niveau de sécurité est le même que celui utilisé par les grandes enseignes de commerce en ligne.
              </p>
            </div>
          </div>

          {/* Section 4: Transparence et confiance */}
          <div className="bg-[#FAF9F8] rounded-lg border-2 border-[#B97A72] p-8 mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mr-4">
                <i className="ri-heart-line text-xl text-white"></i>
              </div>
              <h2 className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Allura, cursive' }}>
                🌸 Transparence et confiance
              </h2>
            </div>
            <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="text-lg leading-relaxed">
                Les Carnets de Lolotte est un atelier artisanal basé dans le sud de la France.
                Chaque commande est traitée personnellement et avec soin.
              </p>
              <p className="text-lg leading-relaxed">
                Vous pouvez acheter en toute sérénité : le paiement est <strong>simple, rapide et protégé</strong> 💕
              </p>
            </div>
          </div>

          {/* Encadré récapitulatif */}
          <div className="bg-[#B97A72] text-white rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-fill text-3xl text-white"></i>
            </div>
            <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Allura, cursive' }}>
              🔐 Récapitulatif
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-xl"></i>
                <span style={{ fontFamily: 'Poppins, sans-serif' }}>Paiement sécurisé via Stripe</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-xl"></i>
                <span style={{ fontFamily: 'Poppins, sans-serif' }}>Données cryptées et non stockées</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-xl"></i>
                <span style={{ fontFamily: 'Poppins, sans-serif' }}>Confirmation immédiate par e-mail</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-xl"></i>
                <span style={{ fontFamily: 'Poppins, sans-serif' }}>Service client réactif en cas de souci</span>
              </div>
            </div>
          </div>

          {/* Message final poétique */}
          <div className="text-center mt-12 p-8">
            <p className="text-xl text-[#C49B86] italic leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
              💕 <em>Merci de faire confiance aux Carnets de Lolotte.</em><br/>
              <em>Chaque achat soutient l'artisanat français et les créations faites main.</em>
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}